import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import './Contact.css'

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        date: '',
        time: '',
    })
    const [params, setParams] = useSearchParams()
    const consult = params.get('type') === 'consultation'
    const timeSlots = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM']
    const choose = (consultation: boolean) => {
        requestId.current = ''
        setSubmitted(false)
        setSubmitError('')
        setParams(consultation ? { type: 'consultation' } : {}, { replace: true })
    }

    const requestId = useRef('')
    const [website, setWebsite] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [submitError, setSubmitError] = useState('')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (isSending) return
        setIsSending(true)
        setSubmitted(false)
        setSubmitError('')
        try {
            if (!requestId.current) requestId.current = crypto.randomUUID()
            const response = await fetch(consult ? '/.netlify/functions/consultation' : '/.netlify/functions/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, website, requestId: requestId.current }),
            })
            const result = await response.json().catch(() => null)
            if (!response.ok || !result?.success) throw new Error('Submission failed')
            setSubmitted(true)
        } catch {
            setSubmitError("Your request could not be sent. Please call (909) 608-1780 or email fitnessresultsactive@gmail.com.")
        } finally {
            setIsSending(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        requestId.current = ''
        setSubmitted(false)
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        })
    }

    return (
        <div className="contact">
            <section className="page-hero">
                <div className="container">
                    <h1 className="animate-slide-up">Contact Us</h1>
                    <p className="page-subtitle animate-slide-up">
                        Questions, concerns, or ready to get started? We're here to listen.
                    </p>
                </div>
            </section>

            <section className="section contact-section">
                <div className="container">
                    <div className="contact-grid">
                        <div className="contact-form-container">
                            <div className="card-glass">
                                <div role="group" aria-label="What would you like to do?" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
                                    <button type="button" aria-pressed={!consult} onClick={() => choose(false)} className={'btn ' + (consult ? 'btn-secondary' : 'btn-primary')} style={{ flex: 1 }}>Send a message</button>
                                    <button type="button" aria-pressed={consult} onClick={() => choose(true)} className={'btn ' + (consult ? 'btn-primary' : 'btn-secondary')} style={{ flex: 1 }}>Book a consultation</button>
                                </div>
                                <h2>{consult ? 'Request Your Consultation' : "Let's Talk About You"}</h2>
                                <p className="form-intro">
                                    {consult
                                        ? "Tell us when works for you. We'll contact you to arrange your consultation; your appointment is not confirmed until we reach you."
                                        : "Tell us about your goals or ask us anything about training. You'll get thoughtful guidance without pressure to sign up."}
                                </p>

                                <p className="form-intro">
                                    <a href="tel:9096081780">Call (909) 608-1780</a>
                                    {' · '}
                                    <a href="mailto:fitnessresultsactive@gmail.com?subject=Fitness%20Results%20Consultation">Email our team</a>
                                </p>

                                {submitted && (
                                    <div className="success-message animate-scale-in">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        <span>{consult ? "Thank you! Your request was sent. We'll contact you to arrange a time." : 'Thank you! Your message was sent. We look forward to talking with you.'}</span>
                                    </div>
                                )}

                                {submitError && <p role="alert">{submitError}</p>}
                                <form name="contact" method="POST" onSubmit={handleSubmit} className="contact-form">
                                    <div hidden aria-hidden="true"><label htmlFor="contact-website">Leave this empty</label><input id="contact-website" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" /></div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="name" className="form-label">Name *</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                maxLength={120}
                                                className="form-input"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="email" className="form-label">Email *</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                maxLength={254}
                                                className="form-input"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="phone" className="form-label">Phone{consult ? ' *' : ''}</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            maxLength={50}
                                            className="form-input"
                                            required={consult}
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {consult ? (
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="date" className="form-label">Preferred Date</label>
                                                <input type="date" id="date" name="date" className="form-input" value={formData.date} onChange={handleChange} min={new Date().toISOString().split('T')[0]} />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="time" className="form-label">Preferred Time</label>
                                                <select id="time" name="time" className="form-select" value={formData.time} onChange={handleChange}>
                                                    <option value="">Any time</option>
                                                    {timeSlots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    ) : (
                                    <div className="form-group">
                                        <label htmlFor="subject" className="form-label">Subject *</label>
                                        <select
                                            id="subject"
                                            name="subject"
                                            className="form-select"
                                            required={!consult}
                                            value={formData.subject}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select a subject</option>
                                            <option value="general">General Inquiry</option>
                                            <option value="membership">Membership Information</option>
                                            <option value="training">Personal Training</option>
                                            <option value="store">Store/Products</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    )}

                                    <div className="form-group">
                                        <label htmlFor="message" className="form-label">Message *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            maxLength={5000}
                                            className="form-textarea"
                                            required={!consult}
                                            placeholder="What would you like help with? Tell us about your goals, experience, or questions."
                                            value={formData.message}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <button type="submit" disabled={isSending} className="btn btn-primary btn-full btn-lg">
                                        {isSending ? 'Sending…' : consult ? 'Request Consultation' : 'Send Message'}
                                    </button>
                                </form>
                            </div>
                        </div>

                        <div className="contact-info-container">
                            <div className="contact-card card-glass">
                                <div className="contact-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>
                                </div>
                                <h3>Visit Us</h3>
                                <p>8920 Vernon Ave., Suite #120<br />Montclair, CA 91763</p>
                                <a href="https://maps.google.com/?q=8920+Vernon+Ave+Suite+%23120+Montclair+CA+91763" target="_blank" rel="noopener noreferrer" className="contact-link">
                                    Get Directions →
                                </a>
                            </div>

                            <div className="contact-card card-glass">
                                <div className="contact-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                    </svg>
                                </div>
                                <h3>Call Us</h3>
                                <p><a href="tel:9096081780">(909) 608-1780</a></p>
                                <p className="hours-text">Mon-Fri: 5 AM - 9 PM<br />Sat: 8 AM - 4 PM</p>
                            </div>

                            <div className="contact-card card-glass">
                                <div className="contact-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                </div>
                                <h3>Email Us</h3>
                                <p><a href="mailto:fitnessresultsactive@gmail.com">fitnessresultsactive@gmail.com</a></p>
                                <p className="response-text">Prefer a conversation? Give us a call.</p>
                            </div>

                            <div className="contact-card card-glass">
                                <h3>Follow Us</h3>
                                <div className="social-links">
                                    <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                        </svg>
                                    </a>
                                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                        </svg>
                                    </a>
                                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-link">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Contact

